import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Receipt,
  Plus,
  Trash2,
  Calculator,
  IndianRupee,
  Percent,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  salaryTabFormatDecimal,
  salaryTabSanitizeDecimal,
  computeSalaryStructureTotalsFromComponents,
  cloneSalaryComponents
} from '../../utils/salaryStructureFormUtils';
import { salaryComponentService } from '../../api/crm/config/configService';

const DEFAULT_NEW_COMPONENT = {
  name: '',
  type: 'earning',
  value: '',
  percentageValue: ''
};

function EmployeeSalaryStructureEditor({
  readOnly = false,
  title,
  hideTitle = false,
  resetKey,
  initialMonthlyCtc = '',
  initialComponents = [],
  onStateChange
}) {
  const amountErrorToastActiveRef = useRef(false);

  const [monthlyCtcInput, setMonthlyCtcInput] = useState(() =>
    initialMonthlyCtc != null ? String(initialMonthlyCtc) : ''
  );
  const [components, setComponents] = useState(() =>
    cloneSalaryComponents(initialComponents)
  );
  const [newComponent, setNewComponent] = useState({ ...DEFAULT_NEW_COMPONENT });
  const [componentNameError, setComponentNameError] = useState(false);
  const [componentNameAlreadyUsed, setComponentNameAlreadyUsed] = useState(false);

  // Reset form only when resetKey changes (e.g. employee switch), not on every parent state echo
  useEffect(() => {
    setMonthlyCtcInput(initialMonthlyCtc != null ? String(initialMonthlyCtc) : '');
    setComponents(cloneSalaryComponents(initialComponents));
    setNewComponent({ ...DEFAULT_NEW_COMPONENT });
    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial* applied on resetKey change only
  }, [resetKey]);

  const componentsSectionEnabled = useMemo(() => {
    const n = parseInt(String(monthlyCtcInput), 10);
    return monthlyCtcInput !== '' && Number.isFinite(n) && n >= 1;
  }, [monthlyCtcInput]);

  const editingEnabled = componentsSectionEnabled && !readOnly;

  const salaryTabTotals = useMemo(() => {
    const raw = computeSalaryStructureTotalsFromComponents(components);
    return {
      earnings: raw.totalEarnings,
      deductions: raw.totalDeductions,
      reimbursements: raw.totalReimbursements,
      net: raw.netPay
    };
  }, [components]);

  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useEffect(() => {
    if (!onStateChangeRef.current) return;
    onStateChangeRef.current({
      monthlyCtc: monthlyCtcInput,
      components,
      totals: salaryTabTotals
    });
  }, [monthlyCtcInput, components, salaryTabTotals]);

  const { data: salaryTabMasterComponents = [], isLoading: loadingSalaryTabComponents } = useQuery({
    queryKey: ['salaryComponents', 'active'],
    queryFn: async () => {
      const result = await salaryComponentService.getAll({ isActive: true });
      return result?.data || [];
    }
  });

  useEffect(() => {
    const name = (newComponent.name || '').trim();
    if (!name) {
      setComponentNameAlreadyUsed(false);
      return;
    }
    const isUsed = components.some((c) => c.name.toLowerCase() === name.toLowerCase());
    setComponentNameAlreadyUsed(isUsed);
    if (isUsed) {
      setComponentNameError(false);
    }
  }, [newComponent.name, components]);

  const getSalaryTabAvailableAmount = useCallback(
    (componentType, excludeComponentId = null) => {
      if (componentType === 'reimbursement') return 100000;
      const n = parseInt(String(monthlyCtcInput), 10);
      const cap =
        monthlyCtcInput !== '' && Number.isFinite(n) && n >= 1 ? n : 10000000;
      const currentNonReimbursementTotal = components
        .filter((comp) => comp.id !== excludeComponentId && comp.type !== 'reimbursement')
        .reduce((sum, comp) => sum + (parseFloat(comp.value) || 0), 0);
      return Math.max(0, cap - currentNonReimbursementTotal);
    },
    [monthlyCtcInput, components]
  );

  const getSalaryTabPercentageFromAmount = useCallback(
    (amount, componentType = 'earning') => {
      if (amount === null || amount === undefined) return null;
      if (componentType === 'reimbursement') {
        const reimbursementLimit = 100000;
        const percentage = (amount / reimbursementLimit) * 100;
        return Math.min(100, Math.round(percentage * 1000) / 1000);
      }
      const ctc = parseInt(String(monthlyCtcInput), 10);
      if (!Number.isFinite(ctc) || ctc < 1) return null;
      const percentage = (amount / ctc) * 100;
      return Math.min(100, Math.round(percentage * 1000) / 1000);
    },
    [monthlyCtcInput]
  );

  const getSalaryTabAmountFromPercentage = useCallback(
    (percentage, componentType = 'earning') => {
      if (percentage === null || percentage === undefined) return null;
      if (componentType === 'reimbursement') {
        const reimbursementLimit = 100000;
        return Math.round((percentage / 100) * reimbursementLimit);
      }
      const ctc = parseInt(String(monthlyCtcInput), 10);
      if (!Number.isFinite(ctc) || ctc < 1) return null;
      return Math.round((percentage / 100) * ctc);
    },
    [monthlyCtcInput]
  );

  const handleSalaryTabMonthlyCtcInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits === '') {
      setMonthlyCtcInput('');
      return;
    }
    const noLeadingZeros = digits.replace(/^0+/, '') || '0';
    if (noLeadingZeros === '0') {
      setMonthlyCtcInput('');
      return;
    }
    let n = parseInt(noLeadingZeros, 10);
    if (!Number.isFinite(n)) {
      setMonthlyCtcInput('');
      return;
    }
    const maxWhole = 100000000;
    if (n > maxWhole) n = maxWhole;
    setMonthlyCtcInput(String(n));
  };

  const handleSalaryTabNewComponentFieldChange = useCallback(
    (field, value) => {
      if (!['value', 'percentageValue'].includes(field)) {
        setNewComponent((prev) => ({ ...prev, [field]: value }));
        return;
      }
      let processedValue = value;
      const decimalPlaces = field === 'percentageValue' ? 3 : 2;
      processedValue = salaryTabSanitizeDecimal(value, decimalPlaces);
      if (processedValue && parseFloat(processedValue) < 0) processedValue = '0';

      setNewComponent((prev) => {
        const compType = prev.type || 'earning';
        const maxAllowed = getSalaryTabAvailableAmount(compType);
        let pv = processedValue;

        if (field === 'value') {
          if (pv && parseFloat(pv) > maxAllowed) {
            pv = String(maxAllowed);
            const limitLabel =
              compType === 'reimbursement' ? 'reimbursement limit (₹1,00,000)' : 'remaining CTC limit';
            if (!amountErrorToastActiveRef.current) {
              toast.error(
                `Amount cannot exceed ${limitLabel} (₹${maxAllowed.toLocaleString('en-IN')}).`
              );
              amountErrorToastActiveRef.current = true;
              setTimeout(() => {
                amountErrorToastActiveRef.current = false;
              }, 4000);
            }
          }
        } else if (field === 'percentageValue' && pv && parseFloat(pv) > 100) {
          pv = '100';
        }

        const updated = {
          ...prev,
          [field]: pv || value
        };

        if (field === 'value') {
          const amountValue = pv ? parseFloat(pv) : 0;
          const derivedPercentage = getSalaryTabPercentageFromAmount(amountValue, compType);
          if (derivedPercentage !== null) {
            updated.percentageValue = salaryTabFormatDecimal(derivedPercentage, 3);
          }
        }

        if (field === 'percentageValue') {
          const percentValue = pv ? parseFloat(pv) : null;
          if (percentValue !== null) {
            let computedAmount = getSalaryTabAmountFromPercentage(percentValue, compType);
            if (computedAmount !== null) {
              const max = getSalaryTabAvailableAmount(compType);
              if (computedAmount > max) {
                computedAmount = max;
                const recalculatedPercentage = getSalaryTabPercentageFromAmount(
                  computedAmount,
                  compType
                );
                updated.percentageValue = salaryTabFormatDecimal(
                  recalculatedPercentage ?? percentValue,
                  3
                );
                const limitLabel =
                  compType === 'reimbursement'
                    ? 'reimbursement limit (₹1,00,000)'
                    : 'remaining CTC limit';
                if (!amountErrorToastActiveRef.current) {
                  toast.error(
                    `Amount derived from percentage exceeds ${limitLabel}. Adjusted automatically.`
                  );
                  amountErrorToastActiveRef.current = true;
                  setTimeout(() => {
                    amountErrorToastActiveRef.current = false;
                  }, 4000);
                }
              }
              updated.value = salaryTabFormatDecimal(computedAmount);
            }
          }
        }

        return updated;
      });
    },
    [getSalaryTabAvailableAmount, getSalaryTabPercentageFromAmount, getSalaryTabAmountFromPercentage]
  );

  const handleSalaryTabExistingComponentFieldChange = useCallback(
    (componentId, field, value) => {
      if (!['value', 'percentageValue'].includes(field)) return;
      let processedValue = value;
      const decimalPlaces = field === 'percentageValue' ? 3 : 2;
      processedValue = salaryTabSanitizeDecimal(value, decimalPlaces);
      if (processedValue && parseFloat(processedValue) < 0) processedValue = '0';

      setComponents((prev) => {
        const idx = prev.findIndex((c) => c.id === componentId);
        if (idx === -1) return prev;
        const row = { ...prev[idx] };
        const compType = row.type || 'earning';

        const availableFor = (type, list) => {
          if (type === 'reimbursement') return 100000;
          const n = parseInt(String(monthlyCtcInput), 10);
          const cap =
            monthlyCtcInput !== '' && Number.isFinite(n) && n >= 1 ? n : 10000000;
          const total = list
            .filter((c) => c.id !== componentId && c.type !== 'reimbursement')
            .reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
          return Math.max(0, cap - total);
        };

        const maxAllowed = availableFor(compType, prev);
        let pv = processedValue;

        if (field === 'value') {
          if (pv && parseFloat(pv) > maxAllowed) {
            pv = String(maxAllowed);
            const limitLabel =
              compType === 'reimbursement' ? 'reimbursement limit (₹1,00,000)' : 'remaining CTC limit';
            if (!amountErrorToastActiveRef.current) {
              toast.error(
                `Amount cannot exceed ${limitLabel} (₹${maxAllowed.toLocaleString('en-IN')}).`
              );
              amountErrorToastActiveRef.current = true;
              setTimeout(() => {
                amountErrorToastActiveRef.current = false;
              }, 4000);
            }
          }
        } else if (field === 'percentageValue' && pv && parseFloat(pv) > 100) {
          pv = '100';
        }

        row[field] = pv || value;

        if (field === 'value') {
          const amountValue = pv ? parseFloat(pv) : 0;
          const derivedPercentage = getSalaryTabPercentageFromAmount(amountValue, compType);
          if (derivedPercentage !== null) {
            row.percentageValue = salaryTabFormatDecimal(derivedPercentage, 3);
          }
        }

        if (field === 'percentageValue') {
          const percentValue = pv ? parseFloat(pv) : null;
          if (percentValue !== null) {
            let computedAmount = getSalaryTabAmountFromPercentage(percentValue, compType);
            if (computedAmount !== null) {
              const max = availableFor(compType, prev);
              if (computedAmount > max) {
                computedAmount = max;
                const recalculatedPercentage = getSalaryTabPercentageFromAmount(
                  computedAmount,
                  compType
                );
                row.percentageValue = salaryTabFormatDecimal(
                  recalculatedPercentage ?? percentValue,
                  3
                );
                const limitLabel =
                  compType === 'reimbursement'
                    ? 'reimbursement limit (₹1,00,000)'
                    : 'remaining CTC limit';
                if (!amountErrorToastActiveRef.current) {
                  toast.error(
                    `Amount derived from percentage exceeds ${limitLabel}. Adjusted automatically.`
                  );
                  amountErrorToastActiveRef.current = true;
                  setTimeout(() => {
                    amountErrorToastActiveRef.current = false;
                  }, 4000);
                }
              }
              row.value = salaryTabFormatDecimal(computedAmount);
            }
          }
        }

        const next = [...prev];
        next[idx] = row;
        return next;
      });
    },
    [getSalaryTabPercentageFromAmount, getSalaryTabAmountFromPercentage, monthlyCtcInput]
  );

  const handleSalaryTabExistingComponentTypeChange = useCallback(
    (componentId, nextType) => {
      setComponents((prev) => {
        const idx = prev.findIndex((c) => c.id === componentId);
        if (idx === -1) return prev;
        const row = { ...prev[idx], type: nextType };

        const availableFor = (type, list) => {
          if (type === 'reimbursement') return 100000;
          const n = parseInt(String(monthlyCtcInput), 10);
          const cap =
            monthlyCtcInput !== '' && Number.isFinite(n) && n >= 1 ? n : 10000000;
          const total = list
            .filter((c) => c.id !== componentId && c.type !== 'reimbursement')
            .reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
          return Math.max(0, cap - total);
        };

        const amt = parseFloat(row.value);
        if (Number.isFinite(amt) && amt > 0) {
          const pct = getSalaryTabPercentageFromAmount(amt, nextType);
          if (pct !== null) {
            row.percentageValue = salaryTabFormatDecimal(pct, 3);
          }
        } else {
          const pctOnly = parseFloat(row.percentageValue);
          if (Number.isFinite(pctOnly) && pctOnly > 0) {
            let computedAmount = getSalaryTabAmountFromPercentage(pctOnly, nextType);
            if (computedAmount !== null) {
              const maxA = availableFor(nextType, prev);
              if (computedAmount > maxA) {
                computedAmount = maxA;
              }
              row.value = salaryTabFormatDecimal(computedAmount);
              const rp = getSalaryTabPercentageFromAmount(parseFloat(row.value) || 0, nextType);
              if (rp !== null) {
                row.percentageValue = salaryTabFormatDecimal(rp, 3);
              }
            }
          }
        }

        const next = [...prev];
        next[idx] = row;
        return next;
      });
    },
    [getSalaryTabPercentageFromAmount, getSalaryTabAmountFromPercentage, monthlyCtcInput]
  );

  const addSalaryTabComponent = () => {
    if (!editingEnabled) return;
    if (!newComponent.name?.trim()) {
      setComponentNameError(true);
      setComponentNameAlreadyUsed(false);
      return;
    }
    const isNameAlreadyUsed = components.some(
      (c) => c.name.toLowerCase() === newComponent.name.trim().toLowerCase()
    );
    if (isNameAlreadyUsed) {
      setComponentNameAlreadyUsed(true);
      setComponentNameError(false);
      return;
    }
    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);
    const id = `salary-tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setComponents((prev) => [
      ...prev,
      {
        id,
        name: newComponent.name.trim(),
        type: newComponent.type,
        value: newComponent.value === '' ? '0' : String(newComponent.value),
        percentageValue:
          newComponent.percentageValue === '' ? '0' : String(newComponent.percentageValue)
      }
    ]);
    setNewComponent({ ...DEFAULT_NEW_COMPONENT });
    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);
  };

  const removeSalaryTabComponent = (componentId) => {
    setComponents((prev) => prev.filter((c) => c.id !== componentId));
  };

  const sectionTitle = title ?? 'Salary Components';

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-3 pb-2 border-b border-gray-200">
          {!hideTitle && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center space-x-2 min-w-0">
                <div className="p-1.5 bg-purple-100 rounded-lg shrink-0">
                  <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{sectionTitle}</h3>
              </div>
              <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full shrink-0 self-start sm:self-center whitespace-nowrap">
                {components.length} {components.length === 1 ? 'Component' : 'Components'}
              </span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 !mt-4">
            <label
              htmlFor="emp-salary-editor-monthly-ctc"
              className="text-lg font-medium text-gray-700 whitespace-nowrap"
            >
              Monthly CTC
            </label>
            <input
              id="emp-salary-editor-monthly-ctc"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={monthlyCtcInput}
              onChange={handleSalaryTabMonthlyCtcInputChange}
              placeholder="e.g. 100000"
              maxLength={9}
              title="Monthly CTC (₹): positive whole numbers only. Maximum 10,00,00,000."
              disabled={readOnly}
              className="w-44 sm:w-52 max-w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          {!readOnly && !componentsSectionEnabled && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Enter a valid Monthly CTC to add and manage salary components.
            </p>
          )}
        </div>

        <div className="space-y-3">
          {!readOnly && (
            <div
              className={`bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-5 rounded-xl border-2 border-dashed border-blue-200 ${
                !editingEnabled ? 'pointer-events-none opacity-55' : ''
              }`}
            >
              <h4 className="text-sm sm:text-md font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Plus className="w-4 h-4 mr-2 text-blue-600" />
                Add New Component
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Component Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newComponent.name}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const row = salaryTabMasterComponents.find((c) => c.name === selectedName);
                      setNewComponent((prev) => ({
                        ...prev,
                        name: selectedName,
                        ...(row ? { type: row.type || 'earning' } : {})
                      }));
                      if (selectedName) {
                        setComponentNameError(false);
                      } else {
                        setComponentNameError(false);
                        setComponentNameAlreadyUsed(false);
                      }
                    }}
                    disabled={!editingEnabled || loadingSalaryTabComponents}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 ${
                      componentNameError || componentNameAlreadyUsed
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-blue-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                  >
                    <option value="" disabled={loadingSalaryTabComponents}>
                      {loadingSalaryTabComponents ? 'Loading components...' : 'Select Component'}
                    </option>
                    {salaryTabMasterComponents.map((comp) => (
                      <option key={comp._id || comp.id || comp.name} value={comp.name}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                  {componentNameError && (
                    <p className="text-xs text-red-600 mt-1">Please select a component name</p>
                  )}
                  {componentNameAlreadyUsed && (
                    <p className="text-xs text-red-600 mt-1">This component name is already used</p>
                  )}
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    value={newComponent.type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setNewComponent((prev) => {
                        const next = { ...prev, type: nextType };
                        const amt = parseFloat(prev.value);
                        if (Number.isFinite(amt) && amt > 0) {
                          const pct = getSalaryTabPercentageFromAmount(amt, nextType);
                          if (pct !== null) {
                            next.percentageValue = salaryTabFormatDecimal(pct, 3);
                          }
                        } else {
                          const pctOnly = parseFloat(prev.percentageValue);
                          if (Number.isFinite(pctOnly) && pctOnly > 0) {
                            let computedAmount = getSalaryTabAmountFromPercentage(pctOnly, nextType);
                            if (computedAmount !== null) {
                              const maxA = getSalaryTabAvailableAmount(nextType);
                              if (computedAmount > maxA) {
                                computedAmount = maxA;
                              }
                              next.value = salaryTabFormatDecimal(computedAmount);
                              const rp = getSalaryTabPercentageFromAmount(
                                parseFloat(next.value) || 0,
                                nextType
                              );
                              if (rp !== null) {
                                next.percentageValue = salaryTabFormatDecimal(rp, 3);
                              }
                            }
                          }
                        }
                        return next;
                      });
                    }}
                    disabled={!editingEnabled}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                    <IndianRupee className="w-3 h-3 mr-1" />
                    Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newComponent.value}
                      onChange={(e) =>
                        handleSalaryTabNewComponentFieldChange('value', e.target.value)
                      }
                      min="0"
                      max={
                        newComponent.type === 'reimbursement'
                          ? 100000
                          : getSalaryTabAvailableAmount(newComponent.type)
                      }
                      disabled={!editingEnabled}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available: ₹
                      {getSalaryTabAvailableAmount(newComponent.type).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                    <Percent className="w-3 h-3 mr-1" />
                    Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newComponent.percentageValue}
                      onChange={(e) =>
                        handleSalaryTabNewComponentFieldChange('percentageValue', e.target.value)
                      }
                      min="0"
                      max="100"
                      disabled={!editingEnabled}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {newComponent.percentageValue || 0}/100%
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2 lg:col-span-3 flex align-center justify-center">
                  <div className="w-full pt-5">
                    <button
                      type="button"
                      onClick={addSalaryTabComponent}
                      disabled={!editingEnabled || componentNameAlreadyUsed}
                      className="w-full px-3 sm:px-4 py-1 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-md hover:shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:from-gray-400 disabled:hover:to-gray-400"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {components.length > 0 && (
            <div className="space-y-3 mt-6">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-purple-600" />
                Added components
              </h4>
              {components.map((component) => (
                <div
                  key={component.id}
                  className="bg-gradient-to-r from-gray-50 to-white p-4 sm:p-5 rounded-xl border border-gray-200"
                >
                  <div className="flex flex-col xl:flex-row xl:items-end gap-4">
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3 sm:gap-4">
                      <div className="sm:col-span-2 xl:col-span-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Component
                        </label>
                        <p
                          className="font-semibold text-gray-900 truncate"
                          title={component.name}
                        >
                          {component.name}
                        </p>
                      </div>
                      <div className="sm:col-span-1 xl:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                        <select
                          value={component.type}
                          onChange={(e) =>
                            handleSalaryTabExistingComponentTypeChange(
                              component.id,
                              e.target.value
                            )
                          }
                          disabled={!editingEnabled}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <option value="earning">Earning</option>
                          <option value="deduction">Deduction</option>
                          <option value="reimbursement">Reimbursement</option>
                        </select>
                      </div>
                      <div className="sm:col-span-1 xl:col-span-2">
                        <label className="text-xs font-medium text-gray-700 mb-1 flex items-center">
                          <IndianRupee className="w-3 h-3 mr-1" />
                          Amount
                        </label>
                        <input
                          type="number"
                          value={component.value}
                          onChange={(e) =>
                            handleSalaryTabExistingComponentFieldChange(
                              component.id,
                              'value',
                              e.target.value
                            )
                          }
                          min="0"
                          max={
                            component.type === 'reimbursement'
                              ? 100000
                              : getSalaryTabAvailableAmount(component.type, component.id)
                          }
                          disabled={!editingEnabled}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Available: ₹
                          {getSalaryTabAvailableAmount(component.type, component.id).toLocaleString(
                            'en-IN'
                          )}
                        </p>
                      </div>
                      <div className="sm:col-span-1 xl:col-span-2">
                        <label className="text-xs font-medium text-gray-700 mb-1 flex items-center">
                          <Percent className="w-3 h-3 mr-1" />
                          Percentage
                        </label>
                        <input
                          type="number"
                          value={component.percentageValue}
                          onChange={(e) =>
                            handleSalaryTabExistingComponentFieldChange(
                              component.id,
                              'percentageValue',
                              e.target.value
                            )
                          }
                          min="0"
                          max="100"
                          disabled={!editingEnabled}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {component.percentageValue || 0}/100%
                        </p>
                      </div>
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeSalaryTabComponent(component.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0 self-end xl:self-center"
                        aria-label="Remove component"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center space-x-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200">
          <div className="p-1.5 bg-green-100 rounded-lg">
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Salary Summary</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Total Earnings
              </label>
              <TrendingUp className="w-4 h-4 text-green-600 flex-shrink-0" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-green-700">
              ₹{salaryTabTotals.earnings.toLocaleString('en-IN')}
            </div>
          </div>

          <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Reimbursements
              </label>
              <Receipt className="w-4 h-4 text-purple-600 flex-shrink-0" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-purple-700">
              ₹{salaryTabTotals.reimbursements.toLocaleString('en-IN')}
            </div>
          </div>

          <div className="bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Total Deductions
              </label>
              <TrendingDown className="w-4 h-4 text-red-600 flex-shrink-0" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-red-700">
              ₹{salaryTabTotals.deductions.toLocaleString('en-IN')}
            </div>
          </div>

          <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Net Pay
              </label>
              <IndianRupee className="w-4 h-4 text-blue-600 flex-shrink-0" />
            </div>
            <div className="text-xl sm:text-2xl font-bold text-blue-700">
              ₹{salaryTabTotals.net.toLocaleString('en-IN')}
            </div>
            <div className="text-xs text-gray-500 mt-1 hidden sm:block">Earnings + Reimbursements</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeSalaryStructureEditor;
